import { Node } from "prosemirror-model";
import { Transaction } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

/** An annotation as passed around internal to this system.  Similar to ExternalAnnotation. */
export interface InternalAnnotation {
	/** Starting index according to the Prosemirror document indexing scheme. */
	from: number;
	/** Ending index according to the Prosemirror document indexing scheme. */
	to: number;
	/** Payload data for this annotation */
	payload: AnnotationPayload;
}

/** An annotation as it appears in STF. */
export interface ExternalAnnotation {
	/** Starting index that this annotation applies to.  Relative to `ScorableTextFormat.text` and computed in units of UTF-16 characters. */
	from: number;
	/** Total number of UTF-16 characters this annotation covers. */
	length: number;
	/** Payload data for this annotation */
	payload: AnnotationPayload;
}

export type AnnotationPayload = HighlighterPayload | CommentPayload;

export type HighlighterColor = "blue" | "green" | "yellow" | "red" | "orange";

/** Represents the student highlighting their own essay as they work on it. */
export interface HighlighterPayload {
	type: "highlighter";
	color: HighlighterColor;
}

/** DraftComment.Type in DraftComment.cs */
export type CommentType = "spelling" | "grammar" | "plagiarism" | "comment";

/** Feedback from the PEG engine, ABE engine, or manually provided teacher feedback. */
export interface CommentPayload {
	type: "comment";
	/** DraftComment.Type in DraftComment.cs */
	commentType: CommentType;
	/** Tracking ID.  Assigned externally, must be unique. */
	id: number;
	/** Plain text of the comment. */
	text: string;
	/** If true, the UI will allow editing the text and deleting the comment. */
	editable: boolean;
}

/** Return true if two annotations are mergeable, based on looking at their payloads. */
function payloadMergeable(x: AnnotationPayload, y: AnnotationPayload) {
	// Never merge non-highlighter annotations, because the represent separate comments even if they contain the same text.
	return x.type === "highlighter" && y.type === "highlighter" && x.color === y.color;
}

/** Map an annotation set through an editing operation. */
export function transactAnnotations(
	tr: Transaction,
	annotations: InternalAnnotation[]
): InternalAnnotation[] {
	if (!tr.mapping.maps.length) {
		return annotations;
	}
	return annotations
		.map(a => ({ from: tr.mapping.map(a.from), to: tr.mapping.map(a.to), payload: a.payload }))
		.filter(a => a.to > a.from); // When text is deleted, no longer relevant highlights collapse on themselves through the mapping.
}

/** Try to combine two annotations if possible. */
function maybeMerge(a: InternalAnnotation, b: InternalAnnotation): InternalAnnotation | null {
	if (!payloadMergeable(a.payload, b.payload)) {
		return null;
	}
	if (a.to < b.from || b.to < a.from) {
		return null;
	}
	return {
		from: Math.min(a.from, b.from),
		to: Math.max(a.to, b.to),
		payload: a.payload, // since payloadMergable, either a.payload or b.payload is equivalent
	};
}

/** Remove a range from an annotation.  Might return the existing annotation unchanged, or 0, 1, or 2 resulting annotations. */
function subtract(a: InternalAnnotation, from: number, to: number): InternalAnnotation[] {
	if (a.to <= from || to <= a.from) {
		return [a];
	}
	return [
		{ from: a.from, to: from, payload: a.payload },
		{ from: to, to: a.to, payload: a.payload },
	].filter(x => x.from < x.to);
}

/** Adds a highlighting to an annotation set */
export function addHighlight(
	annotations: InternalAnnotation[],
	from: number,
	to: number,
	color: HighlighterColor
): InternalAnnotation[] {
	const ret: InternalAnnotation[] = [];
	let next: InternalAnnotation = { from, to, payload: { type: "highlighter", color } };
	for (const a of annotations) {
		if (a.payload.type !== "highlighter") {
			ret.push(a);
		} else if (a.payload.color !== color) {
			// Don't allow multiple highlights over the same text.  I think the old code attempted this, but was sometimes buggy.
			ret.push(...subtract(a, from, to));
		} else {
			const combined = maybeMerge(a, next);
			if (combined) {
				next = combined;
			} else {
				ret.push(a);
			}
		}
	}
	ret.push(next);
	return ret;
}

/** Remove any highlights on the given range from an annotation set. */
export function removeHighlights(
	annotations: InternalAnnotation[],
	from: number,
	to: number
): InternalAnnotation[] {
	const ret: InternalAnnotation[] = [];
	for (const a of annotations) {
		if (a.payload.type !== "highlighter") {
			ret.push(a);
		} else {
			ret.push(...subtract(a, from, to));
		}
	}
	return ret;
}

let nextAnnotationId = -0x80000000;

export function makeNewComment(
	from: number,
	to: number,
	commentType: CommentType
): InternalAnnotation & { payload: CommentPayload } {
	return {
		from,
		to,
		payload: {
			type: "comment",
			commentType,
			id: nextAnnotationId++,
			text: "",
			editable: true,
		},
	};
}

const commentWidgetFactory = (() => {
	function factory(s: string, c: string) {
		const icon =
			s === "comment"
				? `<span class="fa-stack fa-sm"><span class="fas fa-comment fa-stack-1x"></span><span class="far fa-comment fa-stack-1x"></span></span>`
				: `<span class="icon">${c}</span>`;

		const div = document.createElement("div");
		div.innerHTML = `
			<span class="annotation-comment-clicker annotation-comment-clicker-${s}">${icon}</span>
			<span class="annotation-comment-endcap annotation-comment-endcap-${s}"></span>
		`;

		const widgetBase = div.children[0];
		const endcapBase = div.children[1];
		return (from: number, to: number, ids: number[]) => {
			const widget = widgetBase.cloneNode(true) as HTMLSpanElement;
			widget.setAttribute("data-comment-ids", ids.join(","));
			const endcap = endcapBase.cloneNode(true) as HTMLSpanElement;
			return [Decoration.widget(from, widget), Decoration.widget(to, endcap)];
		};
	}
	return {
		spelling: factory("spelling", "S"),
		grammar: factory("grammar", "G"),
		plagiarism: factory("plagiarism", "P"),
		comment: factory("comment", "C"),
		overlap: factory("overlap", "…"),
	};
})();

/** Create a decoration set for rendering a set of annotations in ProseMirror */
export function createDecorations(doc: Node, annotations: InternalAnnotation[]) {
	const decorations: Decoration[] = [];

	/** Internal temp product used when merging comments for display. */
	interface CommentRange {
		from: number;
		to: number;
		annotations: (InternalAnnotation & { payload: CommentPayload })[];
	}
	const commentRanges: CommentRange[] = [];

	for (const a of annotations) {
		const { payload } = a;
		if (payload.type === "highlighter") {
			decorations.push(
				Decoration.inline(a.from, a.to, { class: `annotation-highlight-${payload.color}` })
			);
		} else {
			const x: CommentRange = {
				from: a.from,
				to: a.to,
				annotations: [a as CommentRange["annotations"][0]],
			};

			for (let i = 0; i < commentRanges.length; i++) {
				const y = commentRanges[i];
				if (y.to > x.from && x.to > y.from) {
					// proper overlap; merge and discard y
					x.from = Math.min(x.from, y.from);
					x.to = Math.max(x.to, y.to);
					x.annotations.push(...y.annotations);
					commentRanges.splice(i--, 1);
				}
			}
			commentRanges.push(x);
		}
	}

	for (const r of commentRanges) {
		if (r.annotations.length === 1) {
			// The usual case of a nonoverlapping comment
			const { payload } = r.annotations[0];
			decorations.push(
				Decoration.inline(r.from, r.to, {
					class: `annotation-comment-${payload.commentType}`,
				})
			);
			decorations.push(
				...commentWidgetFactory[payload.commentType](r.from, r.to, [payload.id])
			);
		} else {
			// One of the "grey overlaps".  These have always had poor UX, unfortunately.  Maybe this time will be better?

			r.annotations.sort((x, y) => x.from - y.from || x.to - y.to); // This affects the order that the UI will show these in.

			decorations.push(
				Decoration.inline(r.from, r.to, { class: "annotation-comment-overlap" })
			);
			decorations.push(
				...commentWidgetFactory.overlap(
					r.from,
					r.to,
					r.annotations.map(a => a.payload.id)
				)
			);

			for (const a of r.annotations) {
				const { payload } = a;
				decorations.push(
					Decoration.inline(a.from, a.to, { [`data-shard-of-${payload.id}`]: "" })
				);
			}
		}
	}

	return DecorationSet.create(doc, decorations);
}
